import { isApiError } from '@/lib/api/errors';
import type { ReviewStoryItem } from '@/types/review';

/**
 * Flatten pages into the rendered list, keeping the first occurrence of an id.
 * Overlapping cursor windows can repeat a story across pages; rendering it
 * twice would also duplicate its map pin.
 *
 * Takes anything page-shaped, so the infinite query hands its own pages
 * straight in. There is no longer a `StoryPage` to convert to: the component
 * held pages in its own state, and only the active cursor ever refreshed
 * them.
 */
export function flattenStoryPages(
  pages: readonly { items: ReviewStoryItem[] }[],
): ReviewStoryItem[] {
  const seen = new Set<string>();
  const stories: ReviewStoryItem[] = [];
  for (const page of pages) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      stories.push(item);
    }
  }
  return stories;
}

/**
 * What the control at the foot of the stories list should offer.
 *
 * It used to be derived straight from `query.data?.nextCursor`. The active
 * cursor's query has no `data` while it is in error, so a page that *failed*
 * unmounted the only control that could re-request it — the copy underneath
 * still read "try again" with nothing left to click, and the operator was
 * stranded on a partial list until the 30s poll happened to recover it. Worse
 * for a page the server refuses outright: an unresolvable cursor is a 400, and
 * the poll retries it forever without ever showing a way forward.
 *
 * Deciding from the error state as well keeps a retry reachable. The failed
 * page is retried on its own cursor, so nothing already loaded is discarded
 * and the cursor never advances past a fetch that did not land.
 *
 * That is right for a failure that might not repeat, and wrong for one that
 * certainly will. A cursor the server has *refused* cannot be retried into
 * success, so it resolves to `reset` instead — back to the first page, which is
 * the one page no cursor rejection can apply to. See `isRejectedStoryCursor`.
 */
export type StoryPagerAction =
  | { kind: 'none' }
  | { kind: 'load-more'; cursor: string }
  | { kind: 'retry' }
  | { kind: 'reset' };

/**
 * Has the server refused the cursor this page was fetched with?
 *
 * `/v2/reviews/stories` merges three capped source queries in memory, so a
 * cursor can name a row outside the window the merge can see. The service used
 * to answer that by restarting at page 1 in a shape no caller could tell apart
 * from a legitimate first page; it now answers 400 instead. That is a *verdict
 * on the cursor*, not a hiccup — re-issuing the same cursor produces the same
 * 400 forever, so it must not be retried.
 *
 * Two things make the distinction safe to draw here:
 *
 * - Only a request that *carried* a cursor can be refused this way — the
 *   service reaches the rejection solely inside `if (cursor)`. So a 400 on the
 *   first page is something else, and is left to the transient path: resetting
 *   to the page we are already on would be a no-op loop.
 * - Everything else that can fail here is worth retrying. A network blip
 *   normalizes to status 0 and a fault to 5xx, both of which can succeed on a
 *   second attempt.
 *
 * A cursored 400 raised by something *other* than the cursor — a filter value
 * the server rejects, say — lands in this branch too. That is deliberate: it is
 * equally permanent, so retrying it unchanged is equally futile, and returning
 * to a known-good page is the right move for both. The response carries no
 * machine-readable code to separate them anyway: it is a bare
 * `BadRequestException`, so `status` is the only part a client can branch on.
 */
export function isRejectedStoryCursor(error: unknown, cursor: string | undefined): boolean {
  if (cursor === undefined) return false;
  return isApiError(error) && error.status === 400;
}

export function resolveStoryPagerAction(state: {
  nextCursor?: string;
  isError: boolean;
  error?: unknown;
  /**
   * Did the failure come from fetching a LATER page?
   *
   * Replaces the old `cursor` field, which only existed so this could ask
   * whether one was present. Under an infinite query the component no longer
   * holds a cursor to pass — the query owns them — but it does know whether the
   * failure was a `fetchNextPage`, which is the same question asked directly.
   */
  failedWithCursor?: boolean;
}): StoryPagerAction {
  if (state.isError) {
    // A refused cursor is a dead end: the retry that is right for a blip would
    // re-issue the exact request the server just rejected. Offer the one page
    // that cannot be refused — the first — instead.
    return state.failedWithCursor && isApiError(state.error) && state.error.status === 400
      ? { kind: 'reset' }
      : { kind: 'retry' };
  }
  if (state.nextCursor) return { kind: 'load-more', cursor: state.nextCursor };
  return { kind: 'none' };
}
